import React, { useEffect, useState } from 'react';
import CollapseToggle from '../layout/CollapseToggle';
import Spacer from '../layout/Spacer';

/**
 * designed to act as a collapsible container for a collection of property components, but can be used for general purpose as well
 *
 * internally layouts children components as rows in a table, one per child component
 * @export
 * @param {{
 * title: String,
 * expanded: Boolean,
 * decorators: [React.Component],
 * className: String,
 * headerClassName: String,
 * disableCollapse: Boolean
 * }} props these special props may be used:
 * - title: a text to display at the header line of the group container. this text will always be formatted for display using TextUtils.prettifyText function. *optional*
 * - expanded: determines whether or not the group container should start in the expanded state. *defaults to 'true'. optional*
 * - decorators: an array of components to draw at the end (right side) of the group container's header line, e.g. an icon, button, text, etc. *optional*
 * - className: an additional CSS class name to apply to the root div
 * - headerClassName: an additional CSS class name to apply to the header table cell
 * - disableCollapse: whether to allow the property group to be expanded
 * @returns
 */
export default function SyncedPropertyGroup(props)
{
	const [isExpanded, setIsExpanded] = useState(!props.disableCollapse ? props.locked ? false : props.expanded ?? true : true);

	const syncTarget = Array.isArray(props.decorators) ? props.decorators[0] : props.decorators;
	const synced = syncTarget?.props.synced;
	const showSyncStatus = syncTarget?.props.showSyncStatus;
	const syncStatus = syncTarget?.props.syncStatus;

	useEffect(() =>
	{
		if (props.locked === true && isExpanded)
		{
			setIsExpanded(false);
		}
	}, [props.locked])

	function RenderChildProperties()
	{
		let childrenArray = React.Children.toArray(props.children);
		let result = [];
		for (let i = 0; i < childrenArray.length; i++)
		{
			const child = childrenArray[i];
			if (!child) continue;

			let buildStyle = {}
			if (props.minHeight)
			{
				buildStyle.minHeight = props.minHeight + 'px';
				buildStyle.height = props.minHeight + 'px';
			}

			result.push(
				<tr key={i} style={buildStyle}>
					<td>
						{
							React.isValidElement(child) ?
								React.cloneElement(child) :
								child
						}
					</td>
				</tr>
			);
		}

		return result;
	}

	return (
		<table className={[
			"propertyGroup",
			(props.className ? props.className : ""),
		].join(' ')}
		data-name={props["data-name"] ? props["data-name"] + "-root" : "property-group-" + props.title + "-root"}
		data-drawselected={props.drawSelected}
		data-expanded={isExpanded ? "true" : "false"}
		onClickCapture={props.onClick}
		style={props.style}
		>
			<thead>
				<tr>
					<th className={[
						"propertyGroupHeader",
						(props.headerClassName ? props.headerClassName : ""),
					].join(' ')}
					onDoubleClick={() =>
					{
						if (props.disableCollapse || props.locked) return;
						setIsExpanded(!isExpanded);
						if (props.onToggleCallback)
						{
							props.onToggleCallback(!isExpanded);
						}
					}}
					onContextMenu={(e) =>
					{
						e.preventDefault();
						if (props.onContextMenu)
						{
							props.onContextMenu(e);
						}
					}}
					tooltip={props.tooltip}
					>
						{
							!synced ? null :
								<div className={"syncStatusZone"}>
									{
										!showSyncStatus ? null :
											<div className={"syncStatus"} data-status={syncStatus} />
									}
								</div>
						}
						{
							!props.disableCollapse && !props.locked ?
								<CollapseToggle
									isExpanded={isExpanded}
									onToggleCallback={(v) =>
									{
										setIsExpanded(v);
										if (props.onToggleCallback)
										{
											props.onToggleCallback(v);
										}
									}}
								/> : <></>
						}
						<div className="propertyGroupHeaderText" data-locked={props.locked ? 'true' : 'false'} style={props.headerStyle}>
							{props.title}
						</div>
						<Spacer stretchTo={{ x: "100%" }} />
						{
							!props.decorators ? null :
								Array.isArray(props.decorators) ?
									props.decorators.map((decorator, idx) =>
									{
										return !React.isValidElement(decorator) ? decorator :
											React.cloneElement(decorator, {
												key: idx,
												showSyncStatus: false,
											})
									})
									:
									React.cloneElement(props.decorators, { showSyncStatus: false })
						}
					</th>
				</tr>
			</thead>
			<tbody>
				{
					!isExpanded ? null : RenderChildProperties()
				}
			</tbody>
		</table>
	);
}